// Queries
import { CSSProperties, useEffect, useMemo } from 'react';

import { WATCH_IMAGES } from '../../../../../constants';
import {
  decodeConfigFromUrl,
  getBaseUrlWithoutQuery,
  getFileName,
  isValidBlobUrl,
  isValidUrl,
} from '../../../../../helpers';
import {
  BrandingConfig,
  SelectedFieldInPreview,
  WebsheetTemplateProps,
} from '../../../../../types';
import { FrameSettings } from '../../../../FrameSettingsPanel';
import {
  cancelConfirmationFields,
  defaultActionFields,
  fallBackFields,
  loadingCancelFields,
  loadingReactivateFields,
  loadingSuspendFields,
  managerActionsFields,
  successCancelFields,
  successReactivateFields,
  successSuspendFields,
} from '../constants';
import { useWatchManagerStore } from '../store/manager';

export * from './queries';

export const useWatchImage = (
  values: BrandingConfig,
  deviceType: WebsheetTemplateProps['deviceType'],
  isLinked = false,
) => {
  const isAndroid = deviceType === 'android';

  const pairedImageUrl = isAndroid
    ? values.watch_image_url_android
    : values.watch_image_url;

  const familyPlanImageUrl = isAndroid
    ? values.watch_image_url_android_family_plan
    : values.watch_image_url_family_plan;

  // In 'linked' (sa-watch / Family Plan) mode prefer the Family Plan image,
  // falling back to the paired image when a Family Plan image is not configured.
  const imageUrl = isLinked
    ? familyPlanImageUrl || pairedImageUrl
    : pairedImageUrl;

  const headerImageUrl = useHandleImageUrl(imageUrl);

  const imageConfig = useMemo(
    () => decodeConfigFromUrl(imageUrl || '') as FrameSettings | null,
    [imageUrl],
  );

  return { imageUrl, headerImageUrl, imageConfig };
};

export const useHandleImageUrl = (
  fullWatchImageUrl: string | null | undefined,
) => {
  const watchImageUrl = getBaseUrlWithoutQuery(fullWatchImageUrl || '');

  return useMemo(() => {
    if (watchImageUrl) {
      if (
        // Retro compatibility
        // since Jan 2025, WATCH_IMAGES.none value could be an empty string or 'none' string.
        // Doesnt matter, if is none or empty, image should not be displayed.
        watchImageUrl === WATCH_IMAGES.none.src ||
        watchImageUrl === WATCH_IMAGES.none.name
      ) {
        return false;
      }

      if (isValidUrl(watchImageUrl) || isValidBlobUrl(watchImageUrl)) {
        return watchImageUrl;
      }

      const storedCacheImage = getFileName(watchImageUrl);

      if (storedCacheImage) {
        const storedImageObject =
          WATCH_IMAGES[storedCacheImage as keyof typeof WATCH_IMAGES];
        return storedImageObject?.src ?? false;
      }

      return false;
    }

    return false;
  }, [watchImageUrl]);
};

export const useManagerPreviewSync = ({
  selectedField,
  isPortalPreview,
}: {
  selectedField: SelectedFieldInPreview;
  isPortalPreview: boolean;
}) => {
  const { setManagerValues, resetManagerValues } = useWatchManagerStore();

  const previewData = {
    editable: {
      iccid: '8943174000000141708',
      actions: ['suspend', 'reactivate', 'cancel'],
      details: [
        {
          name: 'Watch Plan A',
          description: 'One-Number Cellular Plan',
          description_extended: '$5 a month on top of your subscription fee',
          locale: 'en-US',
        },
      ],
    },
    noEditable: {
      iccid: '8943174000000141709',
      actions: [],
      details: [
        {
          name: 'Watch Plan B',
          description: '2GB Watch Data Plan',
          description_extended: '$10 a month on top of your subscription fee',
          locale: 'en-US',
        },
      ],
    },
  };

  const actionStates = [
    {
      fields: defaultActionFields,
      values: {
        viewActions: true,
        activeWatch: previewData.editable,
      },
    },
    {
      fields: fallBackFields,
      values: {
        viewActions: true,
        activeWatch: previewData.noEditable,
      },
    },
    {
      fields: loadingSuspendFields,
      values: {
        viewActions: true,
        activeWatch: previewData.editable,
        currentAction: 'suspend',
        actionLoading: true,
      },
    },
    {
      fields: successSuspendFields,
      values: {
        viewActions: true,
        activeWatch: previewData.editable,
        currentAction: 'suspend',
        actionLoading: false,
        actionSucceeded: true,
      },
    },
    {
      fields: loadingCancelFields,
      values: {
        viewActions: true,
        activeWatch: previewData.editable,
        currentAction: 'cancel',
        actionLoading: true,
      },
    },
    {
      fields: successCancelFields,
      values: {
        viewActions: true,
        activeWatch: previewData.editable,
        currentAction: 'cancel',
        actionLoading: false,
        actionSucceeded: true,
      },
    },
    {
      fields: loadingReactivateFields,
      values: {
        viewActions: true,
        activeWatch: previewData.editable,
        currentAction: 'reactivate',
        actionLoading: true,
      },
    },
    {
      fields: successReactivateFields,
      values: {
        viewActions: true,
        activeWatch: previewData.editable,
        currentAction: 'reactivate',
        actionLoading: false,
        actionSucceeded: true,
      },
    },
    {
      fields: cancelConfirmationFields,
      values: {
        activeWatch: previewData.editable,
        viewActions: true,
        currentAction: 'cancel',
        confirmCancel: true,
        actionLoading: true,
      },
    },
  ];

  useEffect(() => {
    if (!isPortalPreview) return;

    resetManagerValues();

    // With no field focused the preview defaults to the actions view.
    if (!selectedField) {
      setManagerValues({
        viewActions: true,
        activeWatch: previewData.editable,
      });
      return;
    }

    const isActionsPreviewActive = [
      ...managerActionsFields,
      ...cancelConfirmationFields,
    ].includes(selectedField.field);

    setManagerValues({ viewActions: isActionsPreviewActive });

    if (!isActionsPreviewActive) return;

    for (const { fields, values } of actionStates) {
      if (fields.includes(selectedField.field)) {
        setManagerValues(values);
        return;
      }
    }
  }, [isPortalPreview, selectedField]);
};
